import { useEffect, useRef, useState } from "react";

interface Preview {
  /** Defaults generated from the offer's current rules. */
  generated: string[];
  /** What users will actually read for this draft. */
  effective: string[];
}

/** Admin editor for ALL of an offer's terms & conditions. Empty = users see the
 *  defaults generated from the offer's rules; once the admin writes any text it
 *  replaces them entirely. "Start from defaults" copies the current defaults
 *  into the box to edit line by line. */
export function TermsEditor({
  value,
  onChange,
  draft,
  loadPreview,
  textareaCls,
  labelCls,
}: {
  value: string;
  onChange: (v: string) => void;
  /** The form's current body; the preview re-fetches (debounced) when it changes. */
  draft: unknown;
  loadPreview: () => Promise<Preview>;
  textareaCls: string;
  labelCls: string;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [failed, setFailed] = useState(false);
  const loadRef = useRef(loadPreview);
  loadRef.current = loadPreview;
  const key = JSON.stringify(draft);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      loadRef.current().then(
        (p) => live && (setPreview(p), setFailed(false)),
        () => live && setFailed(true),
      );
    }, 400);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [key]);

  const custom = value.trim() !== "";
  return (
    <div className="space-y-2">
      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <label className={labelCls + " mb-0"}>Terms &amp; conditions (one per line)</label>
          <span className="flex gap-3 text-xs">
            <button
              type="button"
              className="font-medium text-indigo-400 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!preview}
              onClick={() => preview && onChange(preview.generated.join("\n"))}
            >
              Start from defaults
            </button>
            <button type="button" className="font-medium text-gray-400 hover:underline disabled:opacity-40" disabled={!custom} onClick={() => onChange("")}>
              Reset to automatic
            </button>
          </span>
        </div>
        <textarea
          className={textareaCls}
          rows={8}
          maxLength={1500}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Empty = users see the automatic terms (shown in the preview below). Click “Start from defaults” to edit them."
        />
        <p className="mt-1 text-xs text-gray-500">
          {custom
            ? "Custom terms: users see exactly this text. It will not follow later changes to the offer's rules (dates, caps, minimum), so re-check it when you change them."
            : "Automatic: built from the offer's real rules and kept in sync as you change them."}
        </p>
      </div>
      <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-3">
        <p className="mb-1.5 text-xs font-semibold text-gray-300">
          Preview: what users see under &ldquo;Terms &amp; conditions&rdquo; <span className="ml-1 rounded bg-gray-800 px-1 text-[10px] font-normal text-gray-400">{custom ? "custom" : "automatic"}</span>
        </p>
        {failed && !preview ? (
          <p className="text-xs text-amber-400">Fill in the required fields above to preview the terms.</p>
        ) : !preview ? (
          <p className="text-xs text-gray-500">Loading preview…</p>
        ) : (
          <ul className="list-disc space-y-1 pl-4 text-xs text-gray-300" aria-label="Terms preview">
            {preview.effective.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        )}
        {failed && preview && <p className="mt-1 text-xs text-amber-400">Preview is out of date: some required fields are incomplete.</p>}
      </div>
    </div>
  );
}
